import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Surface, formatINR } from "@/components/shared";
import StatusBadge from "@/components/StatusBadge";
import { PRODUCT } from "@/constants/testIds";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import SearchableSelect from "@/components/SearchableSelect";
import {
  Search, Settings2, FileText, Loader2, ChevronLeft, ChevronRight, Star, X, Image as ImageIcon,
} from "lucide-react";
import { toast } from "sonner";
import { ImageThumb, ImagePreview, useImagePreview } from "@/components/ImagePreview";
import ProductSettingsModal from "@/components/ProductSettingsModal";
import HighlightMatch from "@/components/HighlightMatch";

const PAGE_SIZE = 50;
const SEARCH_SUGGESTION_LIMIT = 10;

function useDebounced(value, ms = 400) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

export default function ProductMaster() {
  const [data, setData] = useState({ items: [], total: 0, total_pages: 1 });
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [suppliers, setSuppliers] = useState([]);

  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("all");
  const [supplierId, setSupplierId] = useState("all");
  const [includeInactive, setIncludeInactive] = useState(false);
  const [hasImageOnly, setHasImageOnly] = useState(false);
  const [view, setView] = useState("table");
  const [page, setPage] = useState(1);

  const [settingsProductId, setSettingsProductId] = useState(null);
  const [settingsProductName, setSettingsProductName] = useState("");
  const [refreshCounter, setRefreshCounter] = useState(0);

  const togglePremium = async (productId, current) => {
    const next = !current;
    // optimistic update
    setData((d) => ({ ...d, items: d.items.map((p) => p.id === productId ? { ...p, is_premium: next } : p) }));
    try {
      await api.patch(`/products/${productId}/premium`, { is_premium: next });
      toast.success(next ? "Marked as Premium" : "Removed Premium mark");
    } catch (err) {
      setData((d) => ({ ...d, items: d.items.map((p) => p.id === productId ? { ...p, is_premium: current } : p) }));
      toast.error(err?.response?.data?.detail || "Failed to update");
    }
  };

  const debouncedSearch = useDebounced(search);
  const effectiveSearch = debouncedSearch.trim().length >= 3 ? debouncedSearch.trim() : "";
  const preview = useImagePreview();

  // Load reference data once
  useEffect(() => {
    (async () => {
      const [c, s] = await Promise.all([api.get("/categories"), api.get("/suppliers")]);
      setCategories(c.data);
      setSuppliers(s.data);
    })();
  }, []);

  // Reset page when filters change
  useEffect(() => { setPage(1); }, [effectiveSearch, category, supplierId, includeInactive, hasImageOnly]);

  // Load paginated products
  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const params = { page, page_size: effectiveSearch ? SEARCH_SUGGESTION_LIMIT : PAGE_SIZE };
        if (effectiveSearch) params.search = effectiveSearch;
        if (category !== "all") params.category = category;
        if (supplierId !== "all") params.supplier_id = supplierId;
        if (includeInactive) params.include_inactive = true;
        if (hasImageOnly) params.has_image = true;
        const res = await api.get("/stock/products", { params });
        setData(res.data);
      } finally {
        setLoading(false);
      }
    })();
  }, [page, effectiveSearch, category, supplierId, includeInactive, hasImageOnly, refreshCounter]);

  const openSettings = (p) => {
    setSettingsProductId(p.id);
    setSettingsProductName(p.name);
  };

  const openPreview = async (productId, name) => {
    const res = await api.get(`/products/${productId}`);
    const imgs = (res.data.images || []).map((im) => ({ ...im, name }));
    if (imgs.length === 0) return;
    preview.open(imgs, 0);
  };

  const clearFilters = () => {
    setSearch(""); setCategory("all"); setSupplierId("all"); setHasImageOnly(false);
  };

  const activeFilterCount =
    (search ? 1 : 0) + (category !== "all" ? 1 : 0) + (supplierId !== "all" ? 1 : 0) + (hasImageOnly ? 1 : 0);
  const startIndex = (page - 1) * PAGE_SIZE;

  return (
    <div className="space-y-2" data-testid={PRODUCT.container}>
      {/* Compact inline filter bar */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative w-44">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
          <Input
            placeholder="Search (min 3 chars)…"
            className="pl-8 pr-7 h-9 text-sm bg-white"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid={PRODUCT.searchInput}
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch("")}
              className="absolute right-1.5 top-1/2 -translate-y-1/2 h-5 w-5 rounded hover:bg-gray-100 flex items-center justify-center text-gray-400 hover:text-gray-700"
              aria-label="Clear search"
            >
              <X className="h-3 w-3" />
            </button>
          )}
        </div>

        <div className="w-52">
          <SearchableSelect
            value={category}
            onChange={setCategory}
            options={categories.map((c) => ({ value: c.name, label: c.name }))}
            placeholder="All Categories"
            allLabel="All Categories"
            testId="product-category-filter"
          />
        </div>

        <div className="w-52">
          <SearchableSelect
            value={supplierId}
            onChange={setSupplierId}
            options={suppliers.map((s) => ({ value: s.id, label: s.name }))}
            placeholder="All Suppliers"
            allLabel="All Suppliers"
            testId="product-supplier-filter"
          />
        </div>

        <button
          type="button"
          onClick={() => setIncludeInactive((v) => !v)}
          className={`hidden h-9 px-2.5 rounded-md border text-xs font-semibold items-center gap-1.5 transition-all ${
            includeInactive
              ? "bg-gray-900 border-gray-900 text-white"
              : "bg-white border-gray-200 text-gray-600 hover:bg-gray-50"
          }`}
          data-testid="product-include-inactive-toggle"
          title={includeInactive ? "Including inactive products" : "Show inactive products"}
        >
          <span className={`h-1.5 w-1.5 rounded-full ${includeInactive ? "bg-emerald-400" : "bg-gray-300"}`} />
          Inactive
        </button>

        <button
          type="button"
          onClick={() => setHasImageOnly((v) => !v)}
          className={`hidden h-9 px-2.5 rounded-md border text-xs font-semibold items-center gap-1.5 transition-all ${
            hasImageOnly
              ? "bg-[#9B1C1C] border-[#9B1C1C] text-white"
              : "bg-white border-gray-200 text-gray-600 hover:bg-gray-50"
          }`}
          data-testid="product-has-image-toggle"
          title={hasImageOnly ? "Showing only products with images" : "Show only products with images"}
        >
          <ImageIcon className="h-3.5 w-3.5" />
          Images only
        </button>

        {activeFilterCount > 0 && (
          <button
            onClick={clearFilters}
            className="h-9 px-2 text-xs font-semibold text-[#9B1C1C] hover:underline"
            data-testid="product-clear-filters"
          >
            Clear ({activeFilterCount})
          </button>
        )}

        <div className="ml-auto flex items-center gap-3">
          <span className="text-xs text-gray-500">
            <span className="font-semibold text-gray-900">{data.total.toLocaleString()}</span> products
            {data.total > 0 && (
              <span className="text-gray-400 ml-2">· page {page}/{data.total_pages}</span>
            )}
          </span>
          <div className="flex items-center gap-1 border border-gray-200 rounded-md p-0.5 bg-white">
            <button
              type="button"
              onClick={() => setView("grid")}
              className={`px-2.5 h-7 text-xs font-medium rounded ${view === "grid" ? "bg-gray-900 text-white" : "text-gray-600 hover:bg-gray-100"}`}
              data-testid="product-view-grid"
            >Grid</button>
            <button
              type="button"
              onClick={() => setView("table")}
              className={`px-2.5 h-7 text-xs font-medium rounded ${view === "table" ? "bg-gray-900 text-white" : "text-gray-600 hover:bg-gray-100"}`}
              data-testid="product-view-table"
            >Table</button>
          </div>
          <Link to="/wings-api-spec" className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#9B1C1C] hover:underline">
            <FileText className="h-3.5 w-3.5" /> API Spec
          </Link>
        </div>
      </div>

      {view === "grid" ? (
        <ProductGrid
          items={data.items}
          loading={loading}
          openPreview={openPreview}
          openSettings={openSettings}
          highlightQuery={effectiveSearch}
        />
      ) : (
        <ProductTable
          items={data.items}
          loading={loading}
          openPreview={openPreview}
          openSettings={openSettings}
          startIndex={startIndex}
          togglePremium={togglePremium}
          highlightQuery={effectiveSearch}
        />
      )}

      <PaginationBar page={page} totalPages={data.total_pages || 1} total={data.total || 0} setPage={setPage} />

      <ProductSettingsModal
        productId={settingsProductId}
        productName={settingsProductName}
        open={!!settingsProductId}
        onOpenChange={(open) => !open && setSettingsProductId(null)}
        onSaved={() => setRefreshCounter((c) => c + 1)}
      />

      {preview.state.open && (
        <ImagePreview
          images={preview.state.images}
          initialIndex={preview.state.index}
          onClose={preview.close}
        />
      )}
    </div>
  );
}

function ProductGrid({ items, loading, openPreview, openSettings, highlightQuery }) {
  return (
    <div className="relative">
      {loading && (
        <div className="absolute inset-0 bg-white/70 backdrop-blur-sm z-10 flex items-center justify-center rounded-md">
          <Loader2 className="h-6 w-6 animate-spin text-[#9B1C1C]" />
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
        {items.map((p) => (
          <div key={p.id} className="group bg-white border border-gray-200 rounded-md overflow-hidden hover:shadow-md hover:-translate-y-0.5 transition-all" data-testid={PRODUCT.row(p.id)}>
            <button
              type="button"
              onClick={() => openPreview(p.id, p.name)}
              className="aspect-square bg-gradient-to-br from-amber-50 to-red-50 relative overflow-hidden w-full"
            >
              <ImageThumb path={p.image_path} name={p.name} size="100%" className="w-full h-full rounded-none" />
            </button>
            <Link to={`/products/${p.id}`} className="block p-4">
              <p className="text-[10px] font-semibold uppercase tracking-widest text-gray-500">
                {p.category}
              </p>
              <p className="font-heading font-semibold text-gray-900 mt-1 line-clamp-2 leading-tight">
                <HighlightMatch text={p.name} query={highlightQuery} />
              </p>
              <p className="text-xs text-gray-500 font-mono mt-1">{p.code}</p>
              {p.suppliers && p.suppliers.length > 0 && (
                <p className="text-[11px] text-gray-500 mt-1.5 truncate" title={p.suppliers.map((s) => s.name).join(", ")}>
                  by <span className="font-medium text-gray-700">{p.suppliers[0].name}</span>
                  {p.suppliers.length > 1 && <> +{p.suppliers.length - 1}</>}
                </p>
              )}
              {p.supplier_aliases && p.supplier_aliases.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-1.5" data-testid={`product-aliases-${p.id}`}>
                  {p.supplier_aliases.slice(0, 3).map((a) => (
                    <span
                      key={a.supplier_id}
                      title={`${a.supplier_name || "Supplier"}: ${a.alias_name || "—"}${a.rate ? ` · ₹${a.rate}` : ""}`}
                      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-blue-50 border border-blue-200 text-[10px] font-medium text-blue-800 max-w-[160px] truncate"
                    >
                      <HighlightMatch text={a.alias_name || a.supplier_name || "—"} query={highlightQuery} />
                      {a.rate !== undefined && a.rate !== null && a.rate !== "" && (
                        <span className="font-mono text-amber-800">₹{Number(a.rate).toLocaleString("en-IN")}</span>
                      )}
                    </span>
                  ))}
                  {p.supplier_aliases.length > 3 && (
                    <span className="text-[10px] text-gray-400">+{p.supplier_aliases.length - 3}</span>
                  )}
                </div>
              )}
              <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100">
                <div>
                  <p className="text-[10px] uppercase tracking-widest text-gray-500">MRP</p>
                  <p className="font-heading font-bold text-amber-700">{formatINR(p.mrp)}</p>
                </div>
                <StatusBadge variant={p.total_stock < p.reorder_level ? "warning" : "success"} dot={false}>
                  {p.total_stock} {p.unit}
                </StatusBadge>
              </div>
            </Link>
            <div className="px-4 pb-3 -mt-1 flex gap-2">
              <Button
                size="sm"
                variant="outline"
                className="flex-1 h-8 text-xs"
                onClick={() => openSettings(p)}
                data-testid={`product-settings-${p.id}`}
              >
                <Settings2 className="h-3 w-3 mr-1" /> Settings
              </Button>
            </div>
          </div>
        ))}
        {!loading && items.length === 0 && (
          <div className="col-span-full text-center py-12 text-sm text-gray-500">No products match the current filter</div>
        )}
      </div>
    </div>
  );
}

function ProductTable({ items, loading, openPreview, openSettings, startIndex, togglePremium, highlightQuery }) {
  return (
    <Surface padded={false}>
      <div className="relative overflow-auto max-h-[calc(100vh-180px)]">
        {loading && (
          <div className="absolute inset-0 bg-white/70 backdrop-blur-sm z-20 flex items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-[#9B1C1C]" />
          </div>
        )}
        <table className="w-full text-sm">
          <thead className="sticky top-0 z-10 shadow-sm">
            <tr className="bg-gray-50 border-b border-gray-200">
              <th className="px-3 py-3 text-left text-[10px] font-semibold text-gray-500 uppercase tracking-wider w-12">#</th>
              <th className="px-2 py-3 text-center text-[10px] font-semibold text-gray-500 uppercase tracking-wider w-12" title="Premium marker">★</th>
              <th className="px-4 py-3 text-left text-[10px] font-semibold text-gray-500 uppercase tracking-wider">Image</th>
              <th className="px-4 py-3 text-left text-[10px] font-semibold text-gray-500 uppercase tracking-wider">Product</th>
              <th className="px-3 py-3 text-left text-[10px] font-semibold text-gray-500 uppercase tracking-wider">Code</th>
              <th className="px-3 py-3 text-left text-[10px] font-semibold text-gray-500 uppercase tracking-wider">Cat / Sub-Cat</th>
              <th className="px-3 py-3 text-left text-[10px] font-semibold text-gray-500 uppercase tracking-wider">Supplier Details</th>
              <th className="px-3 py-3 text-left text-[10px] font-semibold text-gray-500 uppercase tracking-wider">Unit</th>
              <th className="px-3 py-3 text-right text-[10px] font-semibold text-gray-500 uppercase tracking-wider">Stock</th>
              <th className="px-3 py-3 text-left text-[10px] font-semibold text-gray-500 uppercase tracking-wider bg-amber-50/30">Raw Material</th>
              <th className="px-3 py-3 text-center text-[10px] font-semibold text-gray-500 uppercase tracking-wider bg-amber-50/40">Price</th>
              <th className="px-3 py-3 text-right text-[10px] font-semibold text-gray-500 uppercase tracking-wider">MRP</th>
              <th className="px-4 py-3 text-right text-[10px] font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((p, i) => (
              <tr key={p.id} className="border-b border-gray-100 hover:bg-gray-50" data-testid={PRODUCT.row(p.id)}>
                <td className="px-3 py-2.5 text-gray-500 font-mono text-xs">{startIndex + i + 1}</td>
                <td className="px-2 py-2.5 text-center">
                  <button
                    type="button"
                    onClick={() => togglePremium(p.id, !!p.is_premium)}
                    className="p-1 rounded hover:bg-amber-50 transition-colors"
                    title={p.is_premium ? "Remove Premium mark" : "Mark as Premium"}
                    data-testid={`product-premium-${p.id}`}
                  >
                    <Star className={`h-4 w-4 ${p.is_premium ? "fill-amber-400 text-amber-500" : "text-gray-300"}`} />
                  </button>
                </td>
                <td className="px-4 py-2.5">
                  <ImageThumb
                    path={p.image_path}
                    name={p.name}
                    onClick={p.image_path ? () => openPreview(p.id, p.name) : undefined}
                    testId={`product-thumb-${p.id}`}
                  />
                </td>
                <td className="px-4 py-2.5">
                  <Link to={`/products/${p.id}`} className="font-medium text-gray-900 hover:text-[#9B1C1C] truncate block max-w-[260px]">
                    <HighlightMatch text={p.name} query={highlightQuery} />
                  </Link>
                  <p className="text-xs text-gray-500 font-mono">{p.wings_product_id}</p>
                  {p.supplier_aliases && p.supplier_aliases.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-0.5 max-w-[260px]" data-testid={`product-row-aliases-${p.id}`}>
                      {p.supplier_aliases.slice(0, 2).map((a) => (
                        <span
                          key={a.supplier_id}
                          title={`${a.supplier_name || "Supplier"}: ${a.alias_name}`}
                          className="inline-flex items-center px-1.5 py-0.5 rounded bg-blue-50 border border-blue-200 text-[10px] font-medium text-blue-800 truncate max-w-[120px]"
                        >
                          <HighlightMatch text={a.alias_name} query={highlightQuery} />
                        </span>
                      ))}
                      {p.supplier_aliases.length > 2 && (
                        <span className="text-[10px] text-gray-400">+{p.supplier_aliases.length - 2}</span>
                      )}
                    </div>
                  )}
                </td>
                <td className="px-3 py-2.5 font-mono text-xs font-semibold">{p.code}</td>
                <td className="px-3 py-2.5">
                  <p className="text-xs text-gray-900">{p.category}</p>
                  <p className="text-[11px] text-gray-500">{p.subcategory}</p>
                </td>
                <td className="px-3 py-2.5">
                  {(() => {
                    const aliases = p.supplier_aliases || [];
                    const aliasSids = new Set(aliases.map((a) => a.supplier_id));
                    const plain = (p.suppliers || []).filter((s) => !aliasSids.has(s.id));
                    if (aliases.length === 0 && plain.length === 0) {
                      return <span className="text-gray-300 text-xs">—</span>;
                    }
                    return (
                      <div className="flex flex-col gap-1 max-w-[240px]" data-testid={`product-supplier-details-${p.id}`}>
                        {aliases.map((a) => (
                          <div key={a.supplier_id} className="flex items-baseline gap-1.5 min-w-0">
                            <span className="text-xs text-gray-900 font-medium truncate" title={a.supplier_name}>
                              {a.supplier_name || "—"}
                            </span>
                            {a.alias_name && (
                              <span className="text-[11px] text-blue-700 bg-blue-50 border border-blue-100 rounded px-1 truncate max-w-[110px]" title={`Their name: ${a.alias_name}`}>
                                <HighlightMatch text={a.alias_name} query={highlightQuery} />
                              </span>
                            )}
                            {a.rate !== undefined && a.rate !== null && a.rate !== "" && (
                              <span className="text-[11px] font-mono text-amber-800 whitespace-nowrap" title="Buying rate">
                                ₹{Number(a.rate).toLocaleString("en-IN")}
                              </span>
                            )}
                          </div>
                        ))}
                        {plain.slice(0, aliases.length ? 1 : 2).map((s) => (
                          <span key={s.id} className="text-xs text-gray-500 truncate" title={s.name}>{s.name}</span>
                        ))}
                        {plain.length > (aliases.length ? 1 : 2) && (
                          <span className="text-[10px] text-gray-400">
                            +{plain.length - (aliases.length ? 1 : 2)} more
                          </span>
                        )}
                      </div>
                    );
                  })()}
                </td>
                <td className="px-3 py-2.5 text-xs">{p.unit}</td>
                <td className="px-3 py-2.5 text-right">
                  <span className={`font-mono font-semibold ${p.total_stock < p.reorder_level ? "text-amber-700" : "text-gray-900"}`}>
                    {p.total_stock}
                  </span>
                </td>
                <td className="px-3 py-2.5 bg-amber-50/30">
                  {(p.rg_parents && p.rg_parents.length > 0) ? (
                    <div className="max-w-[220px] space-y-0.5">
                      {p.rg_parents.map((r) => (
                        <div key={r.id} className="flex items-center justify-between gap-2">
                          <p className="text-xs text-gray-900 truncate" title={r.name}>{r.name}</p>
                          <span className={`text-[11px] font-mono font-semibold ${r.stock < 0 ? "text-red-600" : "text-amber-800"}`}>
                            {r.stock ?? 0}
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <span className="text-gray-300 text-xs">—</span>
                  )}
                </td>
                <td className="px-3 py-2.5 text-center bg-amber-50/40">
                  {p.price_code ? (
                    <span className="inline-block px-2 py-0.5 rounded bg-amber-100 text-amber-900 font-mono text-xs font-semibold tracking-wider">
                      {p.price_code}
                    </span>
                  ) : (
                    <span className="text-gray-300">—</span>
                  )}
                </td>
                <td className="px-3 py-2.5 text-right font-mono">{formatINR(p.mrp)}</td>
                <td className="px-4 py-2.5 text-right">
                  <div className="flex items-center justify-end gap-1">
                    <Link
                      to={`/products/${p.id}`}
                      className="h-8 px-2 inline-flex items-center text-xs font-semibold text-[#9B1C1C] hover:underline"
                    >
                      Detail
                    </Link>
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-8 text-xs"
                      onClick={() => openSettings(p)}
                      data-testid={`product-settings-${p.id}`}
                    >
                      <Settings2 className="h-3 w-3 mr-1" /> Settings
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
            {!loading && items.length === 0 && (
              <tr><td colSpan={13} className="px-6 py-12 text-center text-sm text-gray-500">No products match the current filter</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </Surface>
  );
}

function PaginationBar({ page, totalPages, total, setPage }) {
  if (totalPages <= 1) return null;
  const canPrev = page > 1;
  const canNext = page < totalPages;
  return (
    <div className="bg-white border border-gray-200 rounded-md px-4 py-2.5 flex items-center gap-3 text-xs text-gray-600" data-testid="product-pagination">
      <span data-testid="product-pagination-info">
        Page <span className="font-semibold text-gray-900">{page}</span> of {totalPages.toLocaleString()} · {total.toLocaleString()} total
      </span>
      <div className="ml-auto flex items-center gap-1">
        <Button size="sm" variant="outline" className="h-8 px-2" disabled={!canPrev} onClick={() => setPage(1)} data-testid="product-pagination-first">« First</Button>
        <Button size="sm" variant="outline" className="h-8 px-2" disabled={!canPrev} onClick={() => setPage(page - 1)} data-testid="product-pagination-prev">
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <Button size="sm" variant="outline" className="h-8 px-2" disabled={!canNext} onClick={() => setPage(page + 1)} data-testid="product-pagination-next">
          <ChevronRight className="h-4 w-4" />
        </Button>
        <Button size="sm" variant="outline" className="h-8 px-2" disabled={!canNext} onClick={() => setPage(totalPages)} data-testid="product-pagination-last">Last »</Button>
      </div>
    </div>
  );
}
